import { h, Component, render } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

function formatNumber(num) {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString('nb-NO');
}

function formatDuration(ms) {
    if (!ms || ms <= 0) return '0s';
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes === 0) return `${seconds}s`;
    return `${minutes}m ${seconds}s`;
}

function formatHours(ms) {
    if (!ms || ms <= 0) return '0';
    const hours = Math.floor(ms / 3600000);
    return formatNumber(hours);
}

function formatMinutes(minutes) {
    if (!minutes || minutes <= 0) return '0m';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
}

@observer
class RaceStatsDrawer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            stats: null,
            isLoading: true,
            error: null,
            period: 'today', // 'today', 'all', 'duringRace', 'custom'
            customStartDate: '',
            customEndDate: '',
        };
    }

    loadStats = async (periodOverride) => {
        const { raceId } = this.props;
        const { period, customStartDate, customEndDate } = this.state;
        const activePeriod = periodOverride || period;

        if (!raceId) {
            this.setState({ error: 'No race ID provided', isLoading: false });
            return;
        }

        this.setState({ isLoading: true });

        try {
            let url = `/api/races/${raceId}/stats?period=${activePeriod}`;
            if (activePeriod === 'custom' && customStartDate && customEndDate) {
                url += `&startDate=${customStartDate}&endDate=${customEndDate}`;
            }
            const response = await util.fetchApi(url, { method: 'GET' });
            if (response.status === 200) {
                this.setState({ stats: response.data, isLoading: false });
            } else {
                this.setState({ error: response.message || 'Failed to load stats', isLoading: false });
            }
        } catch (err) {
            this.setState({ error: err.message, isLoading: false });
        }
    }

    handlePeriodChange = (newPeriod) => {
        this.setState({ period: newPeriod }, () => {
            if (newPeriod !== 'custom') {
                this.loadStats(newPeriod);
            }
        });
    }

    handleDateChange = (field, value) => {
        this.setState({ [field]: value });
    }

    handleCustomDateSubmit = () => {
        const { customStartDate, customEndDate } = this.state;
        if (customStartDate && customEndDate) {
            this.loadStats('custom');
        }
    }

    componentDidMount() {
        this.loadStats();
    }

    onClickPrint = () => {
        const { stats } = this.state;
        if (!stats) return;

        const printContent = this.renderPrintContent(stats);

        // Append to document (same pattern as onClickPrintSale, onClickPrintPrescription)
        const printDiv = document.createElement('div');
        printDiv.className = 'printRaceStats';
        document.body.appendChild(printDiv);
        render(printContent, printDiv);

        // Wait for DOM to update then print
        setTimeout(() => {
            window.print();
            document.body.removeChild(printDiv);
            render(null, printDiv);
        }, 100);
    }

    renderPrintProgressBar(value, maxValue, color = '#0d6efd') {
        const percent = maxValue > 0 ? (value / maxValue) * 100 : 0;
        return (
            <div class='print-progress'>
                <div class='print-progress-bar' style={`width: ${percent}%; background-color: ${color};`} />
            </div>
        );
    }

    renderPrintContent(stats) {
        const {
            race, summary, dailyStats, trafficBreakdown, deviceBreakdown,
            countryBreakdown, cityBreakdown, sessionStats, peakHours, articleStats, raceClasses,
            streamStats,
        } = stats;

        const maxCountryVisitors = countryBreakdown?.length > 0 ? Math.max(...countryBreakdown.map(c => c.visitors)) : 0;
        const maxCityVisitors = cityBreakdown?.length > 0 ? Math.max(...cityBreakdown.map(c => c.visitors)) : 0;

        return (
            <div class='print-race-stats-content'>
                <h4>Statistics: {race?.title}</h4>

                {/* Summary Cards */}
                <div class='summary-grid'>
                    <div class='summary-card'>
                        <div class='label'>Total Views</div>
                        <div class='value'>{formatNumber(summary?.totalViews)}</div>
                    </div>
                    <div class='summary-card'>
                        <div class='label'>Views/Visit</div>
                        <div class='value'>{summary?.uniqueVisitors > 0 ? (summary.totalViews / summary.uniqueVisitors).toFixed(1) : '0'}</div>
                    </div>
                    <div class='summary-card'>
                        <div class='label'>Tracking Views</div>
                        <div class='value'>{formatNumber(summary?.totalLiveTrackingViews)}</div>
                    </div>
                    <div class='summary-card'>
                        <div class='label'>Results Views</div>
                        <div class='value'>{formatNumber(summary?.totalResultViews)}</div>
                    </div>
                    <div class='summary-card'>
                        <div class='label'>Total Signups</div>
                        <div class='value'>{formatNumber(summary?.totalSignups)}</div>
                    </div>
                    <div class='summary-card'>
                        <div class='label'>Unique Visitors</div>
                        <div class='value'>{formatNumber(summary?.uniqueVisitors)}</div>
                    </div>
                    <div class='summary-card'>
                        <div class='label'>Logged In</div>
                        <div class='value'>{formatNumber(summary?.loggedInUsers)}</div>
                    </div>
                    <div class='summary-card'>
                        <div class='label'>Avg Session</div>
                        <div class='value'>{formatDuration(summary?.avgSessionDuration)}</div>
                    </div>
                    <div class='summary-card'>
                        <div class='label'>Peak Hour</div>
                        <div class='value'>{summary?.peakHour || 0}:00</div>
                    </div>
                    <div class='summary-card'>
                        <div class='label'>Mobile</div>
                        <div class='value'>{deviceBreakdown?.mobile || 0}%</div>
                    </div>
                </div>

                {/* Session Stats */}
                {sessionStats && sessionStats.totalSessions > 0 && (
                    <div class='section'>
                        <h5>Session Duration</h5>
                        <div class='stats-row'>
                            <span>Avg Session: <strong>{formatDuration(sessionStats.avgDuration)}</strong></span>
                            <span>Total Hours: <strong>{formatHours(sessionStats.totalDuration)}</strong></span>
                            <span>Sessions: <strong>{formatNumber(sessionStats.totalSessions)}</strong></span>
                        </div>
                    </div>
                )}

                {/* Traffic Breakdown with bars */}
                {trafficBreakdown && (
                    <div class='section'>
                        <h5>Traffic Breakdown</h5>
                        <div class='bar-list'>
                            <div class='bar-item'>
                                <div class='bar-label'>Race Info</div>
                                <div class='bar-value'>{trafficBreakdown.info}%</div>
                                {this.renderPrintProgressBar(trafficBreakdown.info, 100, '#0d6efd')}
                            </div>
                            <div class='bar-item'>
                                <div class='bar-label'>Results</div>
                                <div class='bar-value'>{trafficBreakdown.results}%</div>
                                {this.renderPrintProgressBar(trafficBreakdown.results, 100, '#198754')}
                            </div>
                            <div class='bar-item'>
                                <div class='bar-label'>Live Tracking</div>
                                <div class='bar-value'>{trafficBreakdown.tracking}%</div>
                                {this.renderPrintProgressBar(trafficBreakdown.tracking, 100, '#0dcaf0')}
                            </div>
                            <div class='bar-item'>
                                <div class='bar-label'>Articles</div>
                                <div class='bar-value'>{trafficBreakdown.articles}%</div>
                                {this.renderPrintProgressBar(trafficBreakdown.articles, 100, '#ffc107')}
                            </div>
                        </div>
                    </div>
                )}

                {/* Country Breakdown with bars */}
                {countryBreakdown && countryBreakdown.length > 0 && (
                    <div class='section'>
                        <h5>Visitors by Country</h5>
                        <div class='bar-list'>
                            {countryBreakdown.slice(0, 10).map(c => (
                                <div class='bar-item' key={c.countryCode}>
                                    <div class='bar-label'>{c.country}</div>
                                    <div class='bar-value'>{formatNumber(c.visitors)} ({c.percentage}%)</div>
                                    {this.renderPrintProgressBar(c.visitors, maxCountryVisitors, '#0d6efd')}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* City Breakdown with bars */}
                {cityBreakdown && cityBreakdown.length > 0 && (
                    <div class='section'>
                        <h5>Visitors by City</h5>
                        <div class='bar-list'>
                            {cityBreakdown.slice(0, 10).map((c, idx) => (
                                <div class='bar-item' key={idx}>
                                    <div class='bar-label'>{c.city} ({c.country})</div>
                                    <div class='bar-value'>{formatNumber(c.visitors)} ({c.percentage}%)</div>
                                    {this.renderPrintProgressBar(c.visitors, maxCityVisitors, '#0dcaf0')}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Article Stats */}
                {articleStats && articleStats.length > 0 && (
                    <div class='section'>
                        <h5>Article Views</h5>
                        <table class='stats-table'>
                            <thead>
                                <tr><th>Article</th><th>Views</th></tr>
                            </thead>
                            <tbody>
                                {articleStats.map(a => (
                                    <tr key={a.id}><td>{a.title}</td><td>{formatNumber(a.totalViews)}</td></tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Stream Stats */}
                {streamStats && streamStats.length > 0 && (
                    <div class='section'>
                        <h5>Stream Views</h5>
                        <table class='stats-table'>
                            <thead>
                                <tr><th>Stream</th><th>Views</th><th>Plays</th><th>Watch Time</th><th>Avg Watch</th><th>Viewers</th></tr>
                            </thead>
                            <tbody>
                                {streamStats.map(s => (
                                    <tr key={s.id}>
                                        <td>{s.streamName}</td>
                                        <td>{formatNumber(s.totalViews)}</td>
                                        <td>{formatNumber(s.totalPlays)}</td>
                                        <td>{formatMinutes(s.totalWatchMinutes)}</td>
                                        <td>{formatMinutes(s.avgWatchMinutes)}</td>
                                        <td>{formatNumber(s.uniqueViewers)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Race Class Stats */}
                {raceClasses && raceClasses.length > 0 && (
                    <div class='section'>
                        <h5>Race Class Breakdown</h5>
                        <table class='stats-table'>
                            <thead>
                                <tr><th>Class</th><th>Views</th><th>Signups</th><th>Results</th><th>Tracking</th></tr>
                            </thead>
                            <tbody>
                                {raceClasses.map(rc => (
                                    <tr key={rc.id}>
                                        <td>{rc.title}</td>
                                        <td>{formatNumber(rc.totalViews)}</td>
                                        <td>{formatNumber(rc.totalSignups)}</td>
                                        <td>{formatNumber(rc.totalResultViews)}</td>
                                        <td>{formatNumber(rc.totalLiveTrackingViews)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <div class='print-footer'>
                    Printed: {new Date().toLocaleDateString('nb-NO')} {new Date().toLocaleTimeString('nb-NO')}
                </div>
            </div>
        );
    }

    renderTrend(trend) {
        if (!trend || trend === 0) return null;
        const isPositive = trend > 0;
        return (
            <span class={`ms-1 ${isPositive ? 'text-success' : 'text-danger'}`}>
                {isPositive ? '↑' : '↓'}{Math.abs(trend)}%
            </span>
        );
    }

    renderSummaryCard(title, value, icon, trend, darkmode) {
        return (
            <div class='col-6 col-md-3 mb-3'>
                <div class={`rounded-lg p-3 h-100 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                    <div class='text-muted small mb-1'>
                        <i class={`fa-solid ${icon} me-1`} />{title}
                    </div>
                    <div class='d-flex align-items-baseline'>
                        <span class='h4 mb-0 font-weight-bold'>{formatNumber(value)}</span>
                        {this.renderTrend(trend)}
                    </div>
                </div>
            </div>
        );
    }

    renderDailyChart(dailyStats, darkmode) {
        if (!dailyStats || dailyStats.length === 0) {
            return (
                <div class='text-muted text-center py-4'>
                    No daily data available yet
                </div>
            );
        }

        const maxViews = Math.max(...dailyStats.map(d => (d.views || 0) + (d.resultViews || 0) + (d.liveTrackingViews || 0) + (d.articleViews || 0)));
        const displayData = dailyStats.slice(-30); // Last 30 days
        const barWidth = Math.floor(100 / Math.min(displayData.length, 30));

        return (
            <div class='d-flex align-items-end justify-content-between' style='height: 120px; overflow-x: auto;'>
                {displayData.map((day, idx) => {
                    const total = (day.views || 0) + (day.resultViews || 0) + (day.liveTrackingViews || 0) + (day.articleViews || 0);
                    const viewsHeight = maxViews > 0 ? ((day.views || 0) / maxViews) * 100 : 0;
                    const resultsHeight = maxViews > 0 ? ((day.resultViews || 0) / maxViews) * 100 : 0;
                    const trackingHeight = maxViews > 0 ? ((day.liveTrackingViews || 0) / maxViews) * 100 : 0;
                    const articleHeight = maxViews > 0 ? ((day.articleViews || 0) / maxViews) * 100 : 0;

                    // Format date label (show day number)
                    const dateLabel = day.date ? day.date.split('-')[2] : idx + 1;

                    return (
                        <div
                            key={idx}
                            class='d-flex flex-column align-items-center flex-shrink-0'
                            style={`width: ${barWidth}%; min-width: 12px; max-width: 24px;`}
                            title={`${day.date}: ${formatNumber(total)} total`}
                        >
                            <div class='d-flex flex-column-reverse' style='height: 100px; width: 100%;'>
                                <div class='bg-primary' style={`height: ${viewsHeight}%; min-height: ${day.views > 0 ? '2px' : '0'};`} />
                                <div class='bg-success' style={`height: ${resultsHeight}%; min-height: ${day.resultViews > 0 ? '2px' : '0'};`} />
                                <div class='bg-info' style={`height: ${trackingHeight}%; min-height: ${day.liveTrackingViews > 0 ? '2px' : '0'};`} />
                                <div class='bg-warning' style={`height: ${articleHeight}%; min-height: ${day.articleViews > 0 ? '2px' : '0'};`} />
                            </div>
                            <div class='text-muted small mt-1' style='font-size: 0.55rem;'>
                                {dateLabel}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    renderSessionStats(sessionStats, darkmode) {
        if (!sessionStats || sessionStats.totalSessions === 0) {
            return (
                <div class='text-muted text-center py-3'>
                    No session data available
                </div>
            );
        }

        const { distribution = {} } = sessionStats;
        const totalForDistribution = Object.values(distribution).reduce((sum, val) => sum + val, 0);

        const buckets = [
            { label: 'Under 1 min', key: 'under1min', color: 'bg-danger' },
            { label: '1-5 min', key: '1to5min', color: 'bg-warning' },
            { label: '5-15 min', key: '5to15min', color: 'bg-primary' },
            { label: '15-30 min', key: '15to30min', color: 'bg-success' },
            { label: '30+ min', key: 'over30min', color: 'bg-info' },
        ];

        return (
            <div>
                {/* Summary stats */}
                <div class='row mb-3'>
                    <div class='col-4 text-center'>
                        <div class='h4 mb-0'>{formatDuration(sessionStats.avgDuration)}</div>
                        <div class='text-muted small'>Avg Session</div>
                    </div>
                    <div class='col-4 text-center'>
                        <div class='h4 mb-0'>{formatHours(sessionStats.totalDuration)}</div>
                        <div class='text-muted small'>Total Hours</div>
                    </div>
                    <div class='col-4 text-center'>
                        <div class='h4 mb-0'>{formatNumber(sessionStats.totalSessions)}</div>
                        <div class='text-muted small'>Sessions</div>
                    </div>
                </div>

                {/* Distribution bars */}
                <div class='small text-muted mb-2'>Session Duration Distribution</div>
                {buckets.map(bucket => {
                    const count = distribution[bucket.key] || 0;
                    const percent = totalForDistribution > 0 ? Math.round((count / totalForDistribution) * 100) : 0;
                    return (
                        <div class='mb-2' key={bucket.key}>
                            <div class='d-flex justify-content-between small mb-1'>
                                <span>{bucket.label}</span>
                                <span class='text-muted'>{formatNumber(count)} ({percent}%)</span>
                            </div>
                            <div class='progress' style='height: 8px;'>
                                <div
                                    class={`progress-bar ${bucket.color}`}
                                    style={`width: ${percent}%;`}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    renderVisitorMap(cityBreakdown) {
        const cities = (cityBreakdown || []).filter(c => Number.isFinite(c.lat) && Number.isFinite(c.lon));
        if (cities.length === 0) {
            return null;
        }
        const maxVisitors = Math.max(...cities.map(c => c.visitors));
        const bounds = cities.map(c => [c.lat, c.lon]);

        return (
            <div class='rounded-lg overflow-hidden' style='height: 300px;'>
                <MapContainer
                    bounds={bounds}
                    boundsOptions={{ padding: [40, 40], maxZoom: 10 }}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={false}
                >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                    />
                    {cities.map(c => (
                        <CircleMarker
                            key={`${c.city}-${c.countryCode}`}
                            center={[c.lat, c.lon]}
                            radius={6 + Math.round(14 * Math.sqrt(c.visitors / maxVisitors))}
                            pathOptions={{ color: '#0d6efd', fillColor: '#0d6efd', fillOpacity: 0.4 }}
                        >
                            <Popup>
                                <strong>{c.city}</strong>{c.country ? `, ${c.country}` : ''}<br />
                                {formatNumber(c.visitors)} {c.visitors === 1 ? 'visitor' : 'visitors'}
                            </Popup>
                        </CircleMarker>
                    ))}
                </MapContainer>
            </div>
        );
    }

    renderCountryBreakdown(countryBreakdown, darkmode) {
        if (!countryBreakdown || countryBreakdown.length === 0) {
            return (
                <div class='text-muted text-center py-3'>
                    No country data available
                </div>
            );
        }

        const maxVisitors = Math.max(...countryBreakdown.map(c => c.visitors));

        return (
            <div>
                {countryBreakdown.map((country, idx) => {
                    const barWidth = maxVisitors > 0 ? (country.visitors / maxVisitors) * 100 : 0;
                    return (
                        <div class='mb-2' key={country.countryCode || idx}>
                            <div class='d-flex justify-content-between small mb-1'>
                                <span>
                                    {country.countryCode && <span class='me-2'>{this.getCountryFlag(country.countryCode)}</span>}
                                    {country.country}
                                </span>
                                <span class='text-muted'>{formatNumber(country.visitors)} ({country.percentage}%)</span>
                            </div>
                            <div class='progress' style='height: 8px;'>
                                <div
                                    class='progress-bar bg-primary'
                                    style={`width: ${barWidth}%;`}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    getCountryFlag(countryCode) {
        if (!countryCode || countryCode.length !== 2) return '';
        // Convert country code to flag emoji (regional indicator symbols)
        const codePoints = countryCode
            .toUpperCase()
            .split('')
            .map(char => 127397 + char.charCodeAt(0));
        return String.fromCodePoint(...codePoints);
    }

    renderCityBreakdown(cityBreakdown, darkmode) {
        if (!cityBreakdown || cityBreakdown.length === 0) {
            return (
                <div class='text-muted text-center py-3'>
                    No city data available
                </div>
            );
        }

        const maxVisitors = Math.max(...cityBreakdown.map(c => c.visitors));

        return (
            <div>
                {cityBreakdown.map((city, idx) => {
                    const barWidth = maxVisitors > 0 ? (city.visitors / maxVisitors) * 100 : 0;
                    return (
                        <div class='mb-2' key={`${city.city}-${city.countryCode}` || idx}>
                            <div class='d-flex justify-content-between small mb-1'>
                                <span>
                                    {city.countryCode && <span class='me-2'>{this.getCountryFlag(city.countryCode)}</span>}
                                    {city.city}{city.country ? ` (${city.country})` : ''}
                                </span>
                                <span class='text-muted'>{formatNumber(city.visitors)} ({city.percentage}%)</span>
                            </div>
                            <div class='progress' style='height: 8px;'>
                                <div
                                    class='progress-bar bg-info'
                                    style={`width: ${barWidth}%;`}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    renderPeakHours(peakHours, darkmode) {
        if (!peakHours || peakHours.length === 0) {
            return (
                <div class='text-muted text-center py-3'>
                    No peak hours data available
                </div>
            );
        }

        const maxViews = Math.max(...peakHours.map(p => p.trackingViews || 0));
        const chartHeight = 80;

        return (
            <div>
                <div class='d-flex align-items-end justify-content-between' style={`height: ${chartHeight}px;`}>
                    {peakHours.map((hour, idx) => {
                        const barHeight = maxViews > 0
                            ? Math.max(((hour.trackingViews || 0) / maxViews) * chartHeight, 2)
                            : 2;
                        const isPeak = hour.trackingViews === maxViews && maxViews > 0;

                        return (
                            <div
                                key={idx}
                                class='d-flex flex-column justify-content-end'
                                style={`width: 4%; min-width: 8px; height: ${chartHeight}px;`}
                                title={`${hour.hour}:00 - ${formatNumber(hour.trackingViews)} views`}
                            >
                                <div
                                    class={isPeak ? 'bg-warning' : 'bg-info'}
                                    style={`height: ${barHeight}px; width: 100%; border-radius: 2px 2px 0 0;`}
                                />
                            </div>
                        );
                    })}
                </div>
                <div class='d-flex justify-content-between text-muted small mt-1'>
                    <span>00:00</span>
                    <span>06:00</span>
                    <span>12:00</span>
                    <span>18:00</span>
                    <span>23:00</span>
                </div>
            </div>
        );
    }

    renderArticleStats(articleStats, darkmode) {
        if (!articleStats || articleStats.length === 0) {
            return null; // Don't show section if no articles
        }

        return (
            <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                <h6 class='mb-3'>
                    <i class='fa-solid fa-newspaper me-2' />
                    Article Views
                </h6>
                <div class='table-responsive'>
                    <table class={`table table-sm ${darkmode ? 'table-dark' : ''}`}>
                        <thead>
                            <tr>
                                <th>Article</th>
                                <th class='text-end'>Views</th>
                            </tr>
                        </thead>
                        <tbody>
                            {articleStats.map(article => (
                                <tr key={article.id}>
                                    <td>{article.title}</td>
                                    <td class='text-end'>{formatNumber(article.totalViews)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    renderStreamStats(streamStats, darkmode) {
        if (!streamStats || streamStats.length === 0) {
            return null; // Don't show section if no streams
        }

        return (
            <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                <h6 class='mb-3'>
                    <i class='fa-solid fa-video me-2' />
                    Stream Views
                </h6>
                <div class='table-responsive'>
                    <table class={`table table-sm ${darkmode ? 'table-dark' : ''}`}>
                        <thead>
                            <tr>
                                <th>Stream</th>
                                <th class='text-end'>Views</th>
                                <th class='text-end'>Plays</th>
                                <th class='text-end'>Watch Time</th>
                                <th class='text-end'>Avg Watch</th>
                                <th class='text-end'>Viewers</th>
                            </tr>
                        </thead>
                        <tbody>
                            {streamStats.map(stream => (
                                <tr key={stream.id}>
                                    <td>{stream.streamName}</td>
                                    <td class='text-end'>{formatNumber(stream.totalViews)}</td>
                                    <td class='text-end'>{formatNumber(stream.totalPlays)}</td>
                                    <td class='text-end'>{formatMinutes(stream.totalWatchMinutes)}</td>
                                    <td class='text-end'>{formatMinutes(stream.avgWatchMinutes)}</td>
                                    <td class='text-end'>{formatNumber(stream.uniqueViewers)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    renderTrafficBreakdown(breakdown, darkmode) {
        const items = [
            { label: 'Race Info', value: breakdown?.info || 0, color: 'bg-primary' },
            { label: 'Results', value: breakdown?.results || 0, color: 'bg-success' },
            { label: 'Live Tracking', value: breakdown?.tracking || 0, color: 'bg-info' },
            { label: 'Articles', value: breakdown?.articles || 0, color: 'bg-warning' },
        ];

        return (
            <div>
                {items.map(item => (
                    <div class='mb-2' key={item.label}>
                        <div class='d-flex justify-content-between small mb-1'>
                            <span>{item.label}</span>
                            <span class='text-muted'>{item.value}%</span>
                        </div>
                        <div class='progress' style='height: 8px;'>
                            <div
                                class={`progress-bar ${item.color}`}
                                style={`width: ${item.value}%;`}
                            />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    renderRaceClassTable(raceClasses, darkmode) {
        if (!raceClasses || raceClasses.length === 0) {
            return (
                <div class='text-muted text-center py-3'>
                    No race classes found
                </div>
            );
        }

        return (
            <div class='table-responsive'>
                <table class={`table table-sm ${darkmode ? 'table-dark' : ''}`}>
                    <thead>
                        <tr>
                            <th>Class</th>
                            <th class='text-end'>Views</th>
                            <th class='text-end'>Signups</th>
                            <th class='text-end'>Results</th>
                            <th class='text-end'>Tracking</th>
                        </tr>
                    </thead>
                    <tbody>
                        {raceClasses.map(rc => (
                            <tr key={rc.id}>
                                <td>{rc.title}</td>
                                <td class='text-end'>{formatNumber(rc.totalViews)}</td>
                                <td class='text-end'>{formatNumber(rc.totalSignups)}</td>
                                <td class='text-end'>{formatNumber(rc.totalResultViews)}</td>
                                <td class='text-end'>{formatNumber(rc.totalLiveTrackingViews)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    renderPeriodSelector(darkmode, race) {
        const { period, customStartDate, customEndDate, isLoading } = this.state;
        const hasRaceDates = race?.startDate && race?.endDate;

        return (
            <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                <div class='d-flex flex-wrap align-items-center gap-2'>
                    <span class='text-muted small me-2'>
                        <i class='fa-solid fa-filter me-1' />
                        Period:
                    </span>
                    <div class='btn-group btn-group-sm'>
                        <button
                            type='button'
                            class={`btn ${period === 'today' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => this.handlePeriodChange('today')}
                            disabled={isLoading}
                        >
                            Today
                        </button>
                        <button
                            type='button'
                            class={`btn ${period === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => this.handlePeriodChange('all')}
                            disabled={isLoading}
                        >
                            All time
                        </button>
                        {hasRaceDates && (
                            <button
                                type='button'
                                class={`btn ${period === 'duringRace' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => this.handlePeriodChange('duringRace')}
                                disabled={isLoading}
                            >
                                During race
                            </button>
                        )}
                        <button
                            type='button'
                            class={`btn ${period === 'custom' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => this.handlePeriodChange('custom')}
                            disabled={isLoading}
                        >
                            Custom
                        </button>
                    </div>

                    {period === 'custom' && (
                        <div class='d-flex align-items-center gap-2 ms-2'>
                            <input
                                type='date'
                                class='form-control form-control-sm'
                                style='width: 140px;'
                                value={customStartDate}
                                onChange={(e) => this.handleDateChange('customStartDate', e.target.value)}
                            />
                            <span class='text-muted'>to</span>
                            <input
                                type='date'
                                class='form-control form-control-sm'
                                style='width: 140px;'
                                value={customEndDate}
                                onChange={(e) => this.handleDateChange('customEndDate', e.target.value)}
                            />
                            <button
                                type='button'
                                class='btn btn-sm btn-primary'
                                onClick={this.handleCustomDateSubmit}
                                disabled={!customStartDate || !customEndDate || isLoading}
                            >
                                <i class='fa-solid fa-search' />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    render() {
        const { stats, isLoading, error, period } = this.state;
        const { userStore } = this.props.stores;
        const { user = {} } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        if (isLoading && !stats) {
            return (
                <div class='w-100 d-flex justify-content-center align-items-center' style='min-height: 200px;'>
                    <div class='spinner-border text-primary' role='status'>
                        <span class='visually-hidden'>Loading...</span>
                    </div>
                </div>
            );
        }

        if (error) {
            return (
                <div class='w-100 p-4'>
                    <div class='alert alert-danger'>{error}</div>
                </div>
            );
        }

        if (!stats) {
            return (
                <div class='w-100 p-4'>
                    <div class='alert alert-warning'>No statistics available</div>
                </div>
            );
        }

        const {
            race, summary, dailyStats, trafficBreakdown, deviceBreakdown,
            countryBreakdown, cityBreakdown, sessionStats, peakHours, articleStats, raceClasses,
            streamStats, streamTotals,
        } = stats;
        const visitorMap = this.renderVisitorMap(cityBreakdown);

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='max-width: 960px; padding: 20px; padding-bottom: 100px;'>

                    <div class='d-flex justify-content-between align-items-center mb-4'>
                        <h5 class='mb-0'>
                            <i class='fa-solid fa-chart-line me-2' />
                            Statistics: {race?.title}
                            {isLoading && <span class='spinner-border spinner-border-sm ms-2' />}
                        </h5>
                        <button
                            type='button'
                            class='btn btn-outline-secondary btn-sm'
                            onClick={this.onClickPrint}
                            title='Print statistics'
                        >
                            <i class='fa-solid fa-print me-1' />
                            Print
                        </button>
                    </div>

                    {/* Period Selector */}
                    {this.renderPeriodSelector(darkmode, race)}

                    {/* Summary Cards - Row 1 */}
                    <div class='row'>
                        {/* Only show trend when viewing all-time stats */}
                        {this.renderSummaryCard('Total Views', summary?.totalViews, 'fa-eye', period === 'all' ? summary?.viewsTrend : null, darkmode)}
                        {this.renderSummaryCard('Views/Visit',
                            summary?.uniqueVisitors > 0
                                ? (summary.totalViews / summary.uniqueVisitors).toFixed(1)
                                : '0',
                            'fa-chart-line', null, darkmode)}
                        {this.renderSummaryCard('Tracking Views', summary?.totalLiveTrackingViews, 'fa-map-location-dot', null, darkmode)}
                        {this.renderSummaryCard('Results Views', summary?.totalResultViews, 'fa-trophy', null, darkmode)}
                        {this.renderSummaryCard('Total Signups', summary?.totalSignups, 'fa-user-plus', null, darkmode)}
                    </div>

                    {/* Summary Cards - Row 2 (New metrics) */}
                    <div class='row'>
                        {this.renderSummaryCard('Unique Visitors', summary?.uniqueVisitors, 'fa-users', null, darkmode)}
                        {this.renderSummaryCard('Logged In', summary?.loggedInUsers, 'fa-user-check', null, darkmode)}
                        {this.renderSummaryCard('Avg Session', formatDuration(summary?.avgSessionDuration), 'fa-clock', null, darkmode)}
                        {this.renderSummaryCard('Peak Hour', `${summary?.peakHour || 0}:00`, 'fa-chart-simple', null, darkmode)}
                        {this.renderSummaryCard('Mobile', `${deviceBreakdown?.mobile || 0}%`, 'fa-mobile-screen', null, darkmode)}
                        {this.renderSummaryCard('Stream Views', streamTotals?.totalViews, 'fa-video', null, darkmode)}
                        {this.renderSummaryCard('Stream Plays', streamTotals?.totalPlays, 'fa-play', null, darkmode)}
                        {this.renderSummaryCard('Watch Time', formatMinutes(streamTotals?.totalWatchMinutes), 'fa-clock-rotate-left', null, darkmode)}
                        {this.renderSummaryCard('Stream Viewers', streamTotals?.uniqueViewers, 'fa-user-group', null, darkmode)}
                    </div>

                    {/* Daily Traffic Chart */}
                    <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <div class='d-flex justify-content-between align-items-center mb-3'>
                            <h6 class='mb-0'>
                                <i class='fa-solid fa-chart-bar me-2' />
                                Daily Traffic (Last 30 Days)
                            </h6>
                            <div class='d-flex gap-3 small'>
                                <span><span class='badge bg-primary'>&nbsp;</span> Info</span>
                                <span><span class='badge bg-success'>&nbsp;</span> Results</span>
                                <span><span class='badge bg-info'>&nbsp;</span> Tracking</span>
                                <span><span class='badge bg-warning'>&nbsp;</span> Articles</span>
                            </div>
                        </div>
                        {this.renderDailyChart(dailyStats, darkmode)}
                    </div>

                    {/* Peak Hours Chart */}
                    <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <h6 class='mb-3'>
                            <i class='fa-solid fa-clock me-2' />
                            Peak Hours (Tracking Views by Hour)
                        </h6>
                        {this.renderPeakHours(peakHours, darkmode)}
                    </div>

                    {/* Session Duration Stats (Advertiser Metrics) */}
                    <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <h6 class='mb-3'>
                            <i class='fa-solid fa-stopwatch me-2' />
                            Session Duration (Advertiser Metrics)
                        </h6>
                        {this.renderSessionStats(sessionStats, darkmode)}
                    </div>

                    {/* Traffic Breakdown */}
                    <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <h6 class='mb-3'>
                            <i class='fa-solid fa-chart-pie me-2' />
                            Traffic Breakdown
                        </h6>
                        {this.renderTrafficBreakdown(trafficBreakdown, darkmode)}
                    </div>

                    {/* Visitors Map */}
                    <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <h6 class='mb-3'>
                            <i class='fa-solid fa-map-location-dot me-2' />
                            Visitors Map
                        </h6>
                        {visitorMap || (
                            <div class='text-muted text-center py-4'>
                                <i class='fa-solid fa-map-location-dot d-block mb-2' style='font-size: 1.5rem;' />
                                No visitor locations yet.<br />
                                <span class='small'>
                                    Locations come from visitor sessions on the race page and live tracking.
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Country Breakdown */}
                    <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <h6 class='mb-3'>
                            <i class='fa-solid fa-globe me-2' />
                            Visitors by Country
                        </h6>
                        {this.renderCountryBreakdown(countryBreakdown, darkmode)}
                    </div>

                    {/* City Breakdown */}
                    <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <h6 class='mb-3'>
                            <i class='fa-solid fa-city me-2' />
                            Visitors by City
                        </h6>
                        {this.renderCityBreakdown(cityBreakdown, darkmode)}
                    </div>

                    {/* Article Stats (if any) */}
                    {this.renderArticleStats(articleStats, darkmode)}

                    {/* Stream Stats (if any) */}
                    {this.renderStreamStats(streamStats, darkmode)}

                    {/* Race Class Breakdown */}
                    <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <h6 class='mb-3'>
                            <i class='fa-solid fa-list me-2' />
                            Race Class Breakdown
                        </h6>
                        {this.renderRaceClassTable(raceClasses, darkmode)}
                    </div>

                </div>
            </div>
        );
    }
}

export default RaceStatsDrawer;
